/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { segment } from '@actions/analytics';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { toggleEnrolledCourse } from '@actions/courses';
import {
  REFERRAL_PLUS_HOME,
  REFERRAL_TOUCHPOINT_LOADED
} from '@constants/segment';
import ExternalAppActivation from '@cont/Checkout/ExternalAppActivation';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import { getIsGTPGoal } from '@api/selectors/constants';
import { Spacings, breakpoints } from '@styles/index';
import { getIsSubscribedToLite } from '@selectors/subscription';
import GTPSubscriptionFeedback from '@cont/GTP/GTPSubscriptionFeedback';
import useSubscribed from '@hooks/useSubscribed';
import useShowReferralBanner from '@stores/referralBanner';
import useConstants from '@api/hooks/useConstants';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useGTPSubscriptionFeedbackCards from '@api/hooks/gtp/useGTPSubscriptionFeedbackCards';
import useGTPStaticData from '@api/hooks/gtp/useGTPStaticData';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMe } from '@api/selectors/auth';
import HomeFeedRows from '@cont/HomeFeed';
import useFreeTrialStore from '@cont/NewPreSubscribed/FreeTrial/freeTrialStore';
import useFreeTrial from '@cont/NewPreSubscribed/FreeTrial/useFreeTrial';
import Dynamic from '@base/Dynamic/Dynamic';
import TopBlock from './TopBlock';
import Offerings from './Offerings';
import RenderBrowseFeedBlock from './RenderBrowseFeedRow';
import RenewBanner from './RenewBanner';

const StyledTopBlock = styled(TopBlock)`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const ActiveSubscriptionModal = Dynamic(() =>
  import('@cont/NewPreSubscribed/FreeTrial/ActiveSubscriptionModal')
);

const PlusHome = () => {
  const router = useRouter();
  const goalUID = router.query.goalUID?.toUpperCase();
  const { goalInfo: goalData } = useGoalInfo(goalUID);

  const goalInfo = useMemo(
    () => ({
      name: goalData.name,
      goalSlug: goalData.goalSlug || goalData.slug,
      slug: goalData.slug,
      icon: goalData.icon,
      uid: goalData.uid,
      isRecordedGoal: goalData.isRecordedGoal
    }),
    [goalData]
  );
  const { constants = {} } = useConstants();
  const isGTP = getIsGTPGoal(goalUID)(constants);
  const hasGoalInfo = !!goalInfo;

  const me = useMyInfo(getMe);
  const isSubscribed = useSubscribed(goalUID);
  const { subscriptionFeedbackCards } = useGTPSubscriptionFeedbackCards(
    goalUID,
    isGTP,
    isSubscribed
  );
  const isActivateFreeTrialClicked = useFreeTrialStore(
    (storeState) => storeState.buttonClicked
  );
  const { hasFreeTrialSubscription } = useFreeTrial(goalUID);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const showReferralBanner = useShowReferralBanner();
  const isLiteUser = useSelector(getIsSubscribedToLite(goalUID));
  const hasActiveSubscription = hasActiveSubscriptionToGoal(me, goalUID);

  const { gtpStaticContent = {} } = useGTPStaticData(goalUID, isGTP);
  const { educatorPanelSection } = gtpStaticContent;
  const [showExternalAppActivation, setShowExternalAppActivation] =
    useState(false);
  const dispatch = useDispatchToStore();
  const { icon: goalIcon, goalSlug, name: goalName } = goalInfo;

  const enroll = (course) => () =>
    dispatch(toggleEnrolledCourse(course.uid, !course.isEnrolled));

  const openExternalAppActivation = (feedData) => {
    setShowExternalAppActivation(true);
    dispatch({
      type: 'SET_EXTERNAL_SUBSCRIPTION_DATA',
      subscriptionData: feedData
    });
  };

  useEffect(() => {
    segment.track(REFERRAL_TOUCHPOINT_LOADED, {
      last_primary_source: REFERRAL_PLUS_HOME
    });
  }, []);

  const renderGTPSubscriptionFeedback = () => {
    return subscriptionFeedbackCards?.map((feedbackInfo) => (
      <GTPSubscriptionFeedback
        key={feedbackInfo.card_type}
        goalUID={goalUID}
        feedbackInfo={feedbackInfo}
      />
    ));
  };

  const purchaseSubscription = useSelector(
    ({ checkout }) =>
      checkout.purchaseSubscriptionResponse?.user_subscription
        ?.external_app_subscriptions[0]
  );

  const renderFeedBlock = (feed, index) => {
    return (
      <RenderBrowseFeedBlock
        index={index}
        item={feed}
        lastPrimarySource="Browse"
        type="plus"
        dataAnalytics="goal"
        isLiteUser={isLiteUser}
        isGTP={isGTP}
        hasActiveSubscription={hasActiveSubscription}
        showReferralBanner={showReferralBanner}
        educatorPanelSection={educatorPanelSection}
        openExternalAppActivation={openExternalAppActivation}
        enroll={enroll}
        goalInfo={goalInfo}
      />
    );
  };

  const addExtraBlock = (feeds) => {
    if (!feeds?.results?.length) return feeds;
    const results = feeds?.results || [];
    if (isGTP) {
      return {
        ...feeds,
        results: [...results, { type: 'gtp-educators' }]
      };
    }
    return {
      ...feeds,
      results: [
        ...results.slice(0, 3),
        { type: 'referral' },
        ...results.slice(3)
      ]
    };
  };

  const showFreeTrialNudge =
    isLoggedIn && isActivateFreeTrialClicked && hasFreeTrialSubscription;

  if (!hasGoalInfo) return null;

  return (
    <>
      <StyledTopBlock
        goalIcon={goalIcon}
        goalSlug={goalSlug}
        goalName={goalName}
      />
      <RenewBanner goalInfo={goalInfo} />
      {showExternalAppActivation ? (
        <ExternalAppActivation
          showModal={showExternalAppActivation}
          closeModal={() => setShowExternalAppActivation(false)}
          onActivationComplete={() => {}}
          goalUID={goalUID}
          dataAnalytics="goal"
          purchaseSubscription={purchaseSubscription}
        />
      ) : null}
      {constants?.RECORDED_CONTENT_GOALS?.includes(goalUID) && isLiteUser && (
        <Offerings goalUID={goalUID} goalSlug={goalSlug} />
      )}
      {renderGTPSubscriptionFeedback()}
      <HomeFeedRows
        RenderFeedRow={renderFeedBlock}
        addExtraBlock={addExtraBlock}
      />
      <ActiveSubscriptionModal open={showFreeTrialNudge} />
    </>
  );
};

export default PlusHome;
